/**
 * Whether a chat alert should be raised, as a function rather than an effect.
 *
 * This lives on its own because it is the part that has been wrong twice. The
 * rules read as one sentence each, but they interact: a line can deserve a
 * sound and not a tray notice, or a notice later than its sound, or neither —
 * and the earlier version folded all of that into one branch, which is how the
 * tray notice came to be decided in a single poll tick and then never again.
 *
 * Pure, so the awkward sequences can be written down as tests instead of
 * reasoned about: reading one conversation while another moves, the page
 * hiding a minute after the line arrived, a thread whose only unread line is
 * a rename.
 */

/** What has already been announced, by channel. */
export interface AlertMemory {
  /**
   * The newest `latest` a sound has been made for, or `undefined` before the
   * server has answered once — the difference between "nothing has happened"
   * and "nothing had happened when this page loaded".
   */
  chimed: string | null | undefined;
  /** The newest `latest` that has been put in the tray. */
  noticed: string | null;
}

/** What the poll and the screen say right now. */
export interface AlertInput {
  /** False until the unread poll has answered once. */
  ready: boolean;
  /** Chat switched on, and this person still works here. */
  enabled: boolean;
  /** When the newest unread line somebody wrote was written; null if none. */
  latest: string | null;
  /** Which conversation that line is in. */
  latestThreadId: string | null;
  unread: number;
  /** Whether the page is on screen — a backgrounded WebView is not. */
  visible: boolean;
  pathname: string;
}

export interface AlertDecision {
  /** Play the chime and buzz. */
  chime: boolean;
  /** Put one notice in the phone's tray. */
  notice: boolean;
  /** What to remember afterwards; hand it back on the next call. */
  memory: AlertMemory;
}

/** ISO timestamps order as strings, so a plain comparison is the clock. */
function after(latest: string, mark: string | null): boolean {
  return mark === null || latest > mark;
}

export function decideAlert(input: AlertInput, memory: AlertMemory): AlertDecision {
  const quiet: AlertDecision = { chime: false, notice: false, memory };
  if (!input.ready || !input.enabled) return quiet;

  // The first answer is the baseline. Everything unread at that moment
  // arrived while the app was closed and has already had its chance to be
  // announced; ringing here would greet every launch with a burst.
  if (memory.chimed === undefined) {
    return { chime: false, notice: false, memory: { chimed: input.latest, noticed: input.latest } };
  }

  const { latest, unread, visible } = input;
  if (latest === null || unread === 0) return quiet;

  // Silent only for the conversation the line actually landed in: it is
  // already on screen, and its read marker clears the count within seconds.
  // Another conversation is news, and so is the thread list — that screen
  // marks nothing read.
  const reading =
    visible && input.latestThreadId !== null && input.pathname === `/chat/${input.latestThreadId}`;

  const fresh = latest !== memory.chimed && after(latest, memory.chimed);
  // The tray is asked on every call, not only when `latest` moves: a line
  // that arrived while somebody was looking at another screen must still be
  // waiting for them when they pocket the phone without reading it.
  const untold = !visible && latest !== memory.noticed && after(latest, memory.noticed);

  return {
    chime: fresh && !reading,
    notice: untold,
    memory: {
      chimed: fresh ? latest : memory.chimed,
      noticed: untold ? latest : memory.noticed,
    },
  };
}
